'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, Wifi, WifiOff, AlertTriangle, RefreshCw, HelpCircle, ChevronDown } from 'lucide-react';

interface NodeInfo {
  name: string;
  role: string;
  emoji: string;
  host: string;
  ip: string;
  status: string; // alive | offline | unknown
  alive: boolean;
  lastSeen?: number | null;
  source?: string;
  group?: 'agent' | 'infra';
  core?: boolean;
  order?: number;
}

interface SourceHealth {
  status: string;
  error?: string | null;
  latency_ms?: number | null;
}

interface GossipData {
  ok: boolean;
  timestamp: number;
  gossipIteration: number;
  nodeCount: number;
  order?: string[];
  nodes: Record<string, NodeInfo>;
  meta?: {
    primary_source?: string;
    degraded?: boolean;
    sources?: Record<string, SourceHealth>;
  };
}

const POLL_INTERVAL = 30000;
// 게이트웨이 앞단(Cloudflare 530 등) 일시 단절은 수 초~수십 초에 회복되는 경우가 많아 오류 시 짧게 재시도한다.
const ERROR_RETRY_INTERVAL = 10000;

// 2026-09-28: 고정 5노드 목록(NODE_ORDER) 폐기 — 게이트웨이가 registry 에서 만든 order 를 그대로 따른다.
// 구 게이트웨이(order 필드 없음)는 node.order → 키 순서로 폴백.
function orderedIds(data: GossipData | null): string[] {
  if (!data?.nodes) return [];
  const ids = data.order?.filter(id => data.nodes[id]) ?? Object.keys(data.nodes);
  return [...ids].sort((a, b) => (data.nodes[a].order ?? 999) - (data.nodes[b].order ?? 999));
}

function ago(ts?: number | null) {
  if (!ts) return '기록 없음';
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}초 전`;
  if (s < 3600) return `${Math.floor(s / 60)}분 전`;
  if (s < 86400) return `${Math.floor(s / 3600)}시간 전`;
  return `${Math.floor(s / 86400)}일 전`;
}

// compact: 헤더 한 줄에 들어가는 요약 버튼 — 누르면 전체 패널을 팝오버로 연다(스케줄러 지면 확보, 2026-10-20).
export default function NodeHealthWidget({ isDark = false, compact = false }: { isDark?: boolean; compact?: boolean }) {
  const [data, setData] = useState<GossipData | null>(null);
  const [open, setOpen] = useState(false);
  const [popShift, setPopShift] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchHealth = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    let failed = false;
    try {
      const res = await fetch('/api/nodes-gossip', { cache: 'no-store' });
      if (!res.ok) {
        // 프록시가 통과시킨 실제 오류 사유 표시 (예: "gateway 530", "timeout")
        let reason = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          reason = body.error ? `${body.error}${body.upstream_error ? ` — ${body.upstream_error}` : ''}` : reason;
        } catch { /* 본문 파싱 실패 시 HTTP 코드만 */ }
        throw new Error(reason);
      }
      const d: GossipData = await res.json();
      setData(d);
      setError(null);
      setLastFetch(Date.now());
    } catch (e: unknown) {
      failed = true;
      setError(e instanceof Error ? e.message : 'fetch failed');
    } finally {
      setLoading(false);
      timer.current = setTimeout(fetchHealth, failed ? ERROR_RETRY_INTERVAL : POLL_INTERVAL);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const onVis = () => { if (!document.hidden) fetchHealth(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchHealth]);

  // 팝오버가 오른쪽 화면 밖으로 넘치지 않도록 왼쪽으로 당긴다(모바일 좁은 화면).
  // 열려 있는 동안 resize·회전에도 다시 계산한다.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      if (!boxRef.current) return;
      const left = boxRef.current.getBoundingClientRect().left;
      const width = Math.min(560, window.innerWidth - 32);
      setPopShift(Math.min(0, window.innerWidth - 16 - (left + width)));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const t = isDark
    ? { box: 'border-white/10 bg-white/5', title: 'text-white/80', sub: 'text-white/40', icon: 'text-white/30 hover:text-white/60', groupLabel: 'text-white/30' }
    : { box: 'border-gray-200 bg-white', title: 'text-gray-800', sub: 'text-gray-400', icon: 'text-gray-400 hover:text-gray-600', groupLabel: 'text-gray-400' };

  const renderPanel = () => {
    if (error && !data) {
      return (
        <div className={`mt-2 rounded-xl border p-3 ${isDark ? 'border-red-500/30 bg-red-500/10' : 'border-red-200 bg-red-50'}`}>
          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>
            <AlertTriangle size={16} />
            <span>노드 헬스 연결 실패</span>
            <button onClick={fetchHealth} disabled={loading} className="ml-auto text-xs underline disabled:opacity-50">
              {loading ? '확인 중…' : '다시 시도'}
            </button>
          </div>
          <div className={`mt-1 text-xs pl-6 ${isDark ? 'text-red-300/70' : 'text-red-500/80'}`}>
            사유: {error} · {ERROR_RETRY_INTERVAL / 1000}초 후 자동 재시도
          </div>
        </div>
      );
    }

    if (!data) {
      return <div className={`mt-2 text-xs ${t.sub}`}>노드 헬스 불러오는 중…</div>;
    }

    const degradedSources = data.meta?.degraded
      ? Object.entries(data.meta.sources || {}).filter(([, s]) => s.status !== 'ok')
      : [];

    const ids = orderedIds(data);
    const agents = ids.filter(id => (data.nodes[id].group ?? 'agent') === 'agent');
    const infra = ids.filter(id => data.nodes[id].group === 'infra');
    const aliveCount = agents.filter(id => data.nodes[id].alive).length;

    const chip = (id: string) => {
      const n = data.nodes[id];
      const unknown = n.status === 'unknown';
      const cls = n.alive
        ? (isDark ? 'bg-green-500/10 border-green-500/25 text-green-300' : 'bg-green-50 border-green-200 text-green-700')
        : unknown
          ? (isDark ? 'bg-white/5 border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-400')
          : (isDark ? 'bg-red-500/10 border-red-500/25 text-red-300' : 'bg-red-50 border-red-200 text-red-600');
      const stateLabel = n.alive ? '정상' : unknown ? '확인 불가' : '오프라인';
      return (
        <div
          key={id}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${cls}`}
          title={`${n.name} · ${n.role} · ${n.host} · ${stateLabel} · 마지막 확인 ${ago(n.lastSeen)} · 출처 ${n.source ?? '?'}`}
        >
          <span>{n.emoji}</span>
          <span className="font-medium">{n.name}</span>
          {n.alive ? <Wifi size={10} /> : unknown ? <HelpCircle size={10} /> : <WifiOff size={10} />}
        </div>
      );
    };

    return (
      <div className={`mt-2 rounded-xl border p-3 ${t.box}`}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Activity size={14} className="text-green-500 shrink-0" />
            <span className={`text-sm font-semibold ${t.title}`}>노드 헬스</span>
            <span className={`text-xs truncate ${t.sub}`}>
              에이전트 {aliveCount}/{agents.length} 정상 · {data.meta?.primary_source ?? '출처 미상'} · {ago(lastFetch)}
            </span>
          </div>
          <button onClick={fetchHealth} disabled={loading} className={`transition-colors ${t.icon}`} title="새로고침">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {agents.map(chip)}
          {infra.length > 0 && (
            <>
              <span className={`self-center text-[10px] px-1 ${t.groupLabel}`}>인프라</span>
              {infra.map(chip)}
            </>
          )}
          {ids.length === 0 && <span className={`text-xs ${t.sub}`}>노드 데이터 없음</span>}
        </div>

        {/* 마지막 성공 값은 유지하고, 최근 조회 실패는 경고로만 표시 */}
        {error && (
          <div className={`mt-2 flex items-start gap-1.5 text-[11px] ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
            <AlertTriangle size={12} className="mt-px shrink-0" />
            <span>최근 조회 실패({error}) — {ago(lastFetch)} 값 표시 중, 자동 재시도</span>
          </div>
        )}
        {degradedSources.length > 0 && (
          <div className={`mt-2 flex items-start gap-1.5 text-[11px] ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
            <AlertTriangle size={12} className="mt-px shrink-0" />
            <span>
              데이터 소스 일부 장애:{' '}
              {degradedSources.map(([id, s]) => `${id}=${s.status}${s.error ? `(${s.error})` : ''}`).join(', ')}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (!compact) return renderPanel();

  const agentIds = orderedIds(data).filter(id => (data!.nodes[id].group ?? 'agent') === 'agent');
  const alive = data ? agentIds.filter(id => data.nodes[id].alive).length : 0;
  const level = error && !data ? 'fail' : !data ? 'loading' : alive === agentIds.length && !error ? 'ok' : 'warn';
  const dot = { ok: 'bg-green-500', warn: 'bg-amber-500', fail: 'bg-red-500', loading: 'bg-gray-400 animate-pulse' }[level];
  const summary = level === 'fail' ? '연결 실패' : level === 'loading' ? '확인 중' : `${alive}/${agentIds.length}`;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        title="노드 헬스 펼치기/접기"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-medium transition-colors ${open
          ? (isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900')
          : (isDark ? 'bg-white/5 border-white/10 text-white/70 hover:text-white' : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900')}`}
      >
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <Activity size={13} className="text-green-500" />
        <span className="hidden sm:inline">노드</span>
        <span>{summary}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div style={{ left: popShift }} className={`absolute top-full mt-1 z-50 w-[min(560px,calc(100vw_-_2rem))] shadow-xl rounded-xl [&>div]:mt-0 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
          {renderPanel()}
        </div>
      )}
    </div>
  );
}
